import { useState, useEffect, useCallback } from "react";
import Icon from "@Components/ui/icon/Icon";
import classes from "./HorizontalScrollContainer.module.scss";

interface IHorizontalScrollContainerProps<T> {
  children: React.ReactNode;
  containerRef: React.RefObject<T>;
}

export const HorizontalScrollContainer = <T extends HTMLElement>({
  containerRef,
  children,
}: IHorizontalScrollContainerProps<T>) => {
  const [showNext, setShowNext] = useState(false);
  const [showPrev, setShowPrev] = useState(false);

  const handleScroll = useCallback(() => {
    const container = containerRef.current;

    if (container) {
      const scrollMax = container.scrollWidth - container.clientWidth;
      const scrollLeft = container.scrollLeft;

      const offset = 10;

      setShowNext(scrollLeft + offset < scrollMax);
      setShowPrev(scrollLeft - offset > 0);
    }
  }, [containerRef]);

  const handleNext = () => {
    const container = containerRef.current;
    if (container) {
      const scrollNext = container.clientWidth > 450 ? 0.85 : 0.17;
      container.scrollLeft += container.clientWidth * scrollNext;
    }
  };

  const handlePrev = () => {
    const container = containerRef.current;

    if (container) {
      const scrollPrev = container.clientWidth > 450 ? 0.85 : 0.17;
      container.scrollLeft -= container.clientWidth * scrollPrev;
    }
  };

  useEffect(() => {
    handleScroll();
    const refContainer = containerRef.current;
    refContainer?.addEventListener("scroll", handleScroll, {
      passive: true,
    });

    window.addEventListener("resize", handleScroll);

    return () => {
      window.removeEventListener("resize", handleScroll);
      refContainer?.removeEventListener("scroll", handleScroll);
    };
  }, [containerRef, handleScroll]);

  return (
    <>
      {showPrev && (
        <div
          onClick={handlePrev}
          className={classes["arrow"] + " " + classes["arrow-left"]}
        >
          <Icon iconName="arrow-left" />
        </div>
      )}
      {children}
      {showNext && (
        <div
          onClick={handleNext}
          className={classes["arrow"] + " " + classes["arrow-right"]}
        >
          <Icon iconName="arrow-right" />
        </div>
      )}
    </>
  );
};
